import * as React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import PlaceIcon from '@mui/icons-material/Place';
import LinkIcon from '@mui/icons-material/Link';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import { type Course } from './Course.tsx';

// Distances proposees par une course (champs indicateur_* : "true"/"false").
const DISTANCES: { champ: keyof Course & string; libelle: string }[] = [
  { champ: 'indicateur_5k', libelle: '5 km' },
  { champ: 'indicateur_10k', libelle: '10 km' },
  { champ: 'indicateur_21k', libelle: 'Semi-marathon' },
  { champ: 'indicateur_42k', libelle: 'Marathon' },
];

type Erreurs = Partial<Record<'label' | 'dateest' | 'city' | 'region' | 'lien', string>>;

function courseVide(): Course {
  return {
    id_calendrier: '',
    dateest: '',
    label: '',
    lien: '',
    region: '',
    city: '',
    province: '',
    indicateur_boston: 'false',
    indicateur_5k: 'false',
    indicateur_10k: 'false',
    indicateur_21k: 'false',
    indicateur_42k: 'false',
  };
}

// Course recue du serveur -> valeurs de la fiche : les champs absents ou
// null (ex : site, province facultatifs) deviennent des chaines vides, et
// les indicateurs sont ramenes a "true"/"false".
function normaliser(course: Course): Course {
  const vide = courseVide();
  const valeurs = { ...vide } as Record<string, unknown>;
  (Object.keys(vide) as (keyof Course & string)[]).forEach((champ) => {
    const v = course[champ];
    valeurs[champ] = v === null || v === undefined ? vide[champ] : String(v);
  });
  DISTANCES.concat([{ champ: 'indicateur_boston', libelle: '' }]).forEach(({ champ }) => {
    valeurs[champ] = String(valeurs[champ]).toLowerCase() === 'true' ? 'true' : 'false';
  });
  return { ...course, ...(valeurs as Course) };
}

function valider(c: Course): Erreurs {
  const erreurs: Erreurs = {};
  if (!c.label.trim()) erreurs.label = 'Donne un nom à la course';
  if (!c.dateest) erreurs.dateest = 'Indique la date de la course';
  if (!c.city.trim()) erreurs.city = 'Indique la ville';
  if (c.lien.trim()) {
    try {
      const url = new URL(c.lien.trim());
      if (url.protocol !== 'http:' && url.protocol !== 'https:') throw new Error();
    } catch {
      erreurs.lien = 'Adresse invalide (ex : https://www.macourse.com)';
    }
  }
  return erreurs;
}

function Section({ icone, titre, children }: { icone: React.ReactNode; titre: string; children: React.ReactNode }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, color: 'text.secondary' }}>
        {icone}
        <Typography variant="overline" sx={{ lineHeight: 1, fontWeight: 600, letterSpacing: '0.08em' }}>
          {titre}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}

/**
 * Fiche de saisie d'une course (creation ou modification), organisee en
 * sections : la course, le lieu, les distances proposees.
 */
export default function FicheCourse({
  ouverte,
  course,
  regions,
  provinces,
  onFermer,
  onEnregistrer,
}: {
  ouverte: boolean;
  /** Course a modifier ; absente pour une creation. */
  course?: Course;
  /** Valeurs deja utilisees, proposees en suggestion. */
  regions: string[];
  provinces: string[];
  onFermer: () => void;
  onEnregistrer: (course: Course) => Promise<unknown>;
}) {
  const creation = !course;
  const [valeurs, setValeurs] = React.useState<Course>(course ?? courseVide());
  const [erreurs, setErreurs] = React.useState<Erreurs>({});
  const [enCours, setEnCours] = React.useState(false);
  const [erreurServeur, setErreurServeur] = React.useState<string | null>(null);

  // Reinitialise la fiche a chaque ouverture.
  React.useEffect(() => {
    if (ouverte) {
      setValeurs(course ? normaliser(course) : courseVide());
      setErreurs({});
      setErreurServeur(null);
    }
  }, [ouverte, course]);

  const changer = (champ: keyof Course, valeur: string) => {
    setValeurs((v: Course) => ({ ...v, [champ]: valeur }));
    setErreurs((e: Erreurs) => ({ ...e, [champ]: undefined }));
  };

  const basculer = (champ: keyof Course) =>
    changer(champ, valeurs[champ] === 'true' ? 'false' : 'true');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nouvellesErreurs = valider(valeurs);
    if (Object.keys(nouvellesErreurs).length > 0) {
      setErreurs(nouvellesErreurs);
      return;
    }
    setEnCours(true);
    setErreurServeur(null);
    try {
      await onEnregistrer({
        ...valeurs,
        label: valeurs.label.trim(),
        city: valeurs.city.trim(),
        region: valeurs.region.trim(),
        province: valeurs.province.trim(),
        lien: valeurs.lien.trim(),
      });
      onFermer();
    } catch (err) {
      setErreurServeur((err as Error).message);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={ouverte} onClose={enCours ? undefined : onFermer} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle sx={{ pb: 0.5 }}>{creation ? 'Nouvelle course' : 'Modifier la course'}</DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>
          {creation
            ? 'Ajoute une course à ton calendrier : elle ne sera visible que par toi. Les champs marqués * sont obligatoires.'
            : 'Mets à jour les informations de la course.'}
        </Typography>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: '12px !important' }}>
          {erreurServeur && (
            <Alert severity="error" onClose={() => setErreurServeur(null)}>
              La course n'a pas pu être enregistrée : {erreurServeur}
            </Alert>
          )}
          <Section icone={<DirectionsRunIcon fontSize="small" />} titre="La course">
            <Stack spacing={2}>
              <TextField
                label="Nom de la course"
                placeholder="ex : Marathon de Montréal"
                value={valeurs.label}
                onChange={(e) => changer('label', e.target.value)}
                error={!!erreurs.label}
                helperText={erreurs.label}
                required
                autoFocus
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Date"
                  type="date"
                  value={valeurs.dateest ?? ''}
                  onChange={(e) => changer('dateest', e.target.value)}
                  error={!!erreurs.dateest}
                  helperText={erreurs.dateest}
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={{ minWidth: 190 }}
                />
                <TextField
                  label="Site de la course"
                  placeholder="https://…"
                  value={valeurs.lien}
                  onChange={(e) => changer('lien', e.target.value)}
                  error={!!erreurs.lien}
                  helperText={erreurs.lien ?? 'Facultatif'}
                  type="url"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Stack>
          </Section>

          <Divider />

          <Section icone={<PlaceIcon fontSize="small" />} titre="Lieu">
            <Stack spacing={2}>
              <TextField
                label="Ville"
                placeholder="ex : Montréal"
                value={valeurs.city}
                onChange={(e) => changer('city', e.target.value)}
                error={!!erreurs.city}
                helperText={erreurs.city}
                required
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Autocomplete
                  freeSolo
                  options={regions}
                  inputValue={valeurs.region}
                  onInputChange={(_e, valeur) => changer('region', valeur)}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Région"
                      placeholder="ex : Montréal, Estrie (facultatif)"
                      error={!!erreurs.region}
                      helperText={erreurs.region}
                      
                    />
                  )}
                />
                <Autocomplete
                  freeSolo
                  options={provinces}
                  inputValue={valeurs.province}
                  onInputChange={(_e, valeur) => changer('province', valeur)}
                  sx={{ minWidth: { sm: 200 } }}
                  renderInput={(params) => (
                    <TextField {...params} label="Province / pays" placeholder="ex : QC" helperText="Facultatif" />
                  )}
                />
              </Stack>
            </Stack>
          </Section>

          <Divider />

          <Section icone={<EmojiEventsIcon fontSize="small" />} titre="Distances proposées">
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {DISTANCES.map(({ champ, libelle }) => {
                const active = valeurs[champ] === 'true';
                return (
                  <Chip
                    key={champ}
                    label={libelle}
                    color={active ? 'primary' : 'default'}
                    variant={active ? 'filled' : 'outlined'}
                    onClick={() => basculer(champ)}
                    aria-pressed={active}
                  />
                );
              })}
            </Stack>
            <FormControlLabel
              sx={{ mt: 1.5 }}
              control={
                <Switch
                  checked={valeurs.indicateur_boston === 'true'}
                  onChange={() => basculer('indicateur_boston')}
                />
              }
              label="Course qualificative pour Boston"
            />
          </Section>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onFermer} disabled={enCours}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={enCours}
            startIcon={enCours ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {creation ? 'Ajouter la course' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
